import { Component, Input, OnChanges } from "@angular/core";
import { formatNumberToHuman } from "../number-to-human/number-to-human";
import { sum } from "../utils/sum";

@Component({
  selector: "ab-trend",
  templateUrl: "./trend.component.html",
})
export class TrendComponent implements OnChanges {
  @Input() public countList: any;
  @Input() public dur: string;
  @Input() public statType: string;
  @Input() public neutralStyle: boolean;
  @Input() public performanceTrend: boolean;
  @Input() public increasePositive: boolean;

  public humanReadableTrend: any;
  public trend: any;
  public showTrend: boolean;
  public tooltip: string;

  public ngOnChanges() {
    if (this.countList) {
      this.trend = this.calcChange(this.countList);
      this.humanReadableTrend = this.humanReadableFormat(Math.abs(this.trend));
      // Do not show trends if all the counts are zero.
      this.showTrend = this.trend !== 0;
    }

    if (this.trend && this.dur && this.statType && this.countList) {
      this.tooltip = this.createTooltipText(
        this.trend,
        this.dur,
        this.statType,
        this.countList.length,
      );
    }
  }

  public colorTrend() {
    if (this.neutralStyle) {
      return "color--grey-500";
    }

    if (this.increasePositive ? this.trend > 0 : this.trend < 0) {
      return "color--success";
    } else {
      return "color--danger";
    }
  }

  private humanReadableFormat(trend: number) {
    if (trend > 0 && trend < 1) {
      return "< 1";
    }

    return formatNumberToHuman(trend);
  }

  private createTooltipText(
    trend: any,
    dur: string,
    statType: string,
    countListLength: number,
  ) {
    const durSize = Math.floor(countListLength / 2);
    const durText = (() => {
      switch (dur) {
        case "90d/1d":
          return durSize === 1 ? "day" : "days";
        case "90m/1m":
          return durSize === 1 ? "minute" : "minutes";
        case "90h/1h":
          return durSize === 1 ? "hour" : "hours";
      }
    })();

    let trendDirection;
    if (this.increasePositive ? trend > 0 : trend < 0) {
      trendDirection = this.performanceTrend ? "improvement in" : "fewer";
    } else {
      trendDirection = this.performanceTrend ? "deterioration in" : "more";
    }

    return (
      `${this.humanReadableTrend}% ${trendDirection} ${statType} ` +
      `during the last ${durSize} ${durText} compared to the ` +
      `previous ${durSize} ${durText}`
    );
  }

  // Calculate the percentage change between the sum of the first and second
  // halves of the count list.
  private calcChange(countList: number[]) {
    // Return a 0 trend if the count list is null or has less than 2 elements.
    if (countList === null || countList.length < 2) {
      return 0;
    }

    const period = Math.abs(countList.length / 2);
    const dataTotal = sum(countList.slice(0, period));
    const newDataTot = sum(countList.slice(period, countList.length));

    if (newDataTot === 0 && dataTotal === 0) {
      return 0;
    } else if (newDataTot === 0) {
      return -100;
    } else if (dataTotal === 0) {
      return 100;
    }

    const trend = ((newDataTot - dataTotal) / dataTotal) * 100;
    if (Math.abs(trend) < 1) {
      return trend;
    }

    return Math.round(trend);
  }
}
